import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, Col, Row, Select, Spin } from "antd";
import { getCategoriesNameList, getCompanyCategoryReviews } from "apis/competitorPulseApis";
import BarChartCompetitorReview from "./BarChart";
import APIError from "components/Common/APIError";

const CategoryWiseSpread = ({ onChangeTab, companiesList, companiesLoading }) => {

  const legendItems = [
    { label: "Positive", color: "#04BFC4" },
    { label: "Neutral", color: "#EAEBEB" },
    { label: "Negative", color: "#F8766D" },
  ]
  const [filterValue, setFilterValue] = useState({
    category: "All",
    defaultCompany: [],
    company: null,
  });
  const {
    data: categoryList,
    isLoading: categoryDataLoading,
    isError: categoryDataIsError,
    error: categoryDataError,
  } = useQuery({
    queryKey: ["categoryList"],
    queryFn: () => getCategoriesNameList(),
    keepPreviousData: true,
    refetchOnWindowFocus: false,
  });

  const sortedCompanies = useMemo(() => {
    if (!companiesList?.companies) return [];

    const mainCompanies = Object.entries(companiesList.companies)
      .filter(([key]) => key !== "Other")
      .map(([key, value]) => ({ company: key, value }));

    const otherCompanies = companiesList.companies.Other?.companies
      ? Object.entries(companiesList.companies.Other.companies).map(([key, value]) => ({
          company: key,
          value,
        }))
      : [];

    return [...mainCompanies, ...otherCompanies].sort((a, b) => b.value - a.value);
  }, [companiesList]);


  useMemo(() => {
    if (sortedCompanies.length) {
        const defaultCompany = sortedCompanies.slice(0, 8).map((item) => item.company);
      setFilterValue((prev) => ({
        ...prev,
        defaultCompany,
        company: defaultCompany,
      }));
    }
  }, [sortedCompanies]);

  const {
    data: companyCategoryReviewData,
    isLoading: companyCategoryReviewLoading,
    isError: companyCategoryReviewIsError,
    error: companyCategoryReviewError,
  } = useQuery({
    queryKey: ["companyCategoryReviews", filterValue.category, filterValue.company],
    queryFn: () =>
      getCompanyCategoryReviews(
        filterValue?.category === "All" ? null : filterValue?.category,
        filterValue.company
      ),
    keepPreviousData: true,
    enabled: !!(filterValue.category && filterValue.company),
    refetchOnWindowFocus: false,
  });


  const onFilterChange = (name, value) => {
    if (name === "category") {
      setFilterValue((prev) => ({
        ...prev,
        [name]: value || "All",
      }));
    } else if (name === "company") {
      setFilterValue((prev) => ({
        ...prev,
        [name]: !value?.length ? prev?.defaultCompany : value,
      }));
    }
  };

  return (
    <Card
      title={
        <>
          <div className="sub-header">Sentiment Spread</div>
          <div className="card-sub-text">
            Understand how customer sentiment is regarding manufacturers and in specific categories
          </div>
        </>
      }
      bordered={false}
    >
      <Row className="company-wise-review-card">
        <Col span={24} >
      

        
<Row gutter={[16, 16]}>
  <Col xs={24} sm={24} md={6} lg={6} xl={4}>
    <div className="lable">Categories</div>
    <Select
      allowClear
      size="large"
      placeholder="Select a Category"
      className="w100 mt05"
      value={filterValue.category}
      loading={categoryDataLoading}
      maxTagCount="responsive"
      onChange={(value) => onFilterChange("category", value)}
      >
      <Select.Option value="All">All</Select.Option>
      {categoryList?.categories?.map((items, index) => (
        <Select.Option key={index} value={items}>
          {items}
        </Select.Option>
      ))}
    </Select>
  </Col>

  <Col xs={24} sm={24} md={6} lg={6} xl={4}>
    <div className="lable">Company</div>
    <Select
      allowClear
      size="large"
      mode="multiple"
      maxCount={12}
      placeholder="Select Companies"
      className="w100 mt05"
      value={filterValue.company}
      loading={companiesLoading}
      maxTagCount="responsive"
      onChange={(value) => onFilterChange("company", value)}
      >
      {sortedCompanies.map((item, index) => (
        <Select.Option
          disabled={
            filterValue.company?.length < 5 &&
            filterValue.company?.includes(item.company)
          }
          key={index}
          value={item.company}
        >
          {item.company}
        </Select.Option>
      ))}
    </Select>
  </Col>
  <Col xs={24} sm={24} md={12} lg={12} xl={16} style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-end' }}>
    <div className="legend-container">
      {legendItems.map((item, index) => (
        <div key={item.label} className="legend-item">
          <div
            className="legend-color-box"
            style={{ backgroundColor: item.color }}
          ></div>
          <span className="legend-label">{item.label}</span>
          {index < legendItems.length - 1 && (
            <div className="legend-separator">|</div>
          )}
        </div>
      ))}
    </div>
  </Col>
</Row>
        </Col>
        </Row>
        <Col span={24} className="mt2">
          {categoryDataIsError || companyCategoryReviewIsError ? (
            <Row className="tree-map ml1">
              <APIError
                message={
                  categoryDataError?.response?.data?.message ||
                  companyCategoryReviewError?.response?.data?.message
                }
              />
            </Row>
          ) : categoryDataLoading || companyCategoryReviewLoading ? (
            <div className="tree-map flex-center">
              <Spin tip="Loading" size="large" />
            </div>
          ) : (
            <BarChartCompetitorReview
            data={{
              ...companyCategoryReviewData,
              selectedCompanies: filterValue.company,
            }}
              selectedCategories={filterValue?.category}
              onChangeTab={onChangeTab}
            />
          )}
        </Col>
      
    </Card>
  );
};

export default CategoryWiseSpread;
